import { timeTrackingEventT } from "dora-contracts";
import api, { noContentDecoder } from "../../api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getPositionForDriverEventFromWatch } from "../../../helpers/position";
import { DateTime } from "luxon";

const prefix = "app/cargo-time-tracking";

type State = {
  cargoTimeTrackingRequestsInProgress: {
    [cargoId: string]: {
      start: boolean;
      pause: boolean;
      resume: boolean;
      stop: boolean;
    };
  };
};

const initialState: State = {
  cargoTimeTrackingRequestsInProgress: {},
};

const reqWithEndpoint = (endpoint: string) => {
  return createAsyncThunk(
    `${prefix}/${endpoint}`,
    async (
      input: {
        cargoId: string;
        data: {
          time: DateTime;
          truckId: string;
          routeId: string;
          stopId: string;
        };
      },
      thunkApi
    ) => {
      const position = await getPositionForDriverEventFromWatch();
      return api(thunkApi).post({
        url: `/drivers-api/cargos/${input.cargoId}/time-tracking/${endpoint}`,
        body: timeTrackingEventT.encode({
          ...input.data,
          position: position
            ? {
                lat: position.lat,
                lon: position.lon,
                accuracy: position.accuracy,
              }
            : null,
        }),
        decoder: noContentDecoder,
      });
    }
  );
};

export const startCargoTimeTracking = reqWithEndpoint("start");
export const pauseCargoTimeTracking = reqWithEndpoint("pause");
export const resumeCargoTimeTracking = reqWithEndpoint("resume");
export const stopCargoTimeTracking = reqWithEndpoint("stop");

const defaultCargoState = {
  start: false,
  pause: false,
  resume: false,
  stop: false,
};

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(startCargoTimeTracking.pending, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          start: true,
        };
      })
      .addCase(startCargoTimeTracking.fulfilled, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          start: false,
        };
      })
      .addCase(startCargoTimeTracking.rejected, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          start: false,
        };
      })
      .addCase(pauseCargoTimeTracking.pending, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          pause: true,
        };
      })
      .addCase(pauseCargoTimeTracking.fulfilled, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          pause: false,
        };
      })
      .addCase(pauseCargoTimeTracking.rejected, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          pause: false,
        };
      })
      .addCase(resumeCargoTimeTracking.pending, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          resume: true,
        };
      })
      .addCase(resumeCargoTimeTracking.fulfilled, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          resume: false,
        };
      })
      .addCase(resumeCargoTimeTracking.rejected, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          resume: false,
        };
      })
      .addCase(stopCargoTimeTracking.pending, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          stop: true,
        };
      })
      .addCase(stopCargoTimeTracking.fulfilled, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          stop: false,
        };
      })
      .addCase(stopCargoTimeTracking.rejected, (state, action) => {
        state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId] = {
          ...defaultCargoState,
          ...state.cargoTimeTrackingRequestsInProgress[action.meta.arg.cargoId],
          stop: false,
        };
      });
  },
});

export default slice.reducer;
